{
  "title": "System Dashboard",
  "heroTitle": "Ask anything about your data.",
  "heroDescription": "Investigate incidents, root cause analysis, and analyze logs in plain English.",
  "preferencesUpdated": "Preferences updated",
  "unableToUpdatePreferences": "Unable to update preferences",
  "widgets": {
    "logAnalysisHistogram": "Log Analysis Histogram",
    "totalIngressVolume": "Total Ingress Volume",
    "totalEgressVolume": "Total Egress Volume",
    "recentActivity": "Recent Activity"
  },
  "aiAssistant": {
    "title": "Assistant",
    "description": "For the best results, keep your prompt short and simple.",
    "placeholder": "Ask a question or use one of the prompt buttons below to get started."
  },
  "navigation": {
    "goToViews": "Go to Live Tail",
    "gotoScopedView": "Go to scoped in View",
    "goToDashboard": "Go to Dashboard",
    "goToPipelines": "Go to Pipelines",
    "buildPipeline": "Build a Pipeline",
    "getStarted": "Get Started",
    "viewAllAuditEvents": "View all audit events",
    "goToMCP": "Go to MCP",
    "goToServiceMaps": "Go to Service Maps",
    "goToTraceInsights": "Go to Trace Insights",
    "goToLogInsights": "Go to Log Insights"
  },
  "tabs": {
    "auditLogs": "Audit Logs",
    "errors": "Errors"
  },
  "quicklinks": {
    "logAnalysis": {
      "title": "Log Analysis",
      "description": "Analyze logs in real time using Live Tail and save queries with Views."
    },
    "logInsights": {
      "title": "Log Insights",
      "description": "Surface patterns, anomalies, and trends across your apps, hosts, and nodes."
    },
    "traceInsights": {
      "title": "Trace Insights"
    },
    "serviceMaps": {
      "title": "Service Maps",
      "description": "Visualize service dependencies and trace requests across your systems."
    },
    "pipelines": {
      "title": "Pipelines",
      "description": "Route, filter, and reduce log volume and costs before it hits downstream tools."
    },
    "optimizeData": {
      "title": "Optimize Data",
      "description": "Build a pipeline to reduce log volume with our workflow"
    },
    "documentation": {
      "title": "Documentation",
      "description": "Guides, API reference, and tutorials."
    },
    "mcp": {
      "title": "Model Context Protocol",
      "description": "Connect Mezmo to your AI tools."
    },
    "aura": {
      "title": "AURA",
      "description": "Mezmo's open-source harness for production-ready agents."
    }
  },
  "messages": {
    "noDataFound": "No data found during the selected time period",
    "noAuditEvents": "No audit events found during the selected time period",
    "noPipelineErrors": "None of the pipeline are reporting any errors.",
    "sameAs": "same as",
    "higher": "higher",
    "lower": "lower",
    "thanPreviousTimePeriod": "than previous time period",
    "wasEventOn": "was {{ event }}",
    "onThe": "on the",
    "onThePipeline": "on the pipeline",
    "transformGroup": "transform group",
    "by": "by",
    "at": "at",
    "the": "The"
  },
  "dashboard": {
    "overview": "Overview",
    "updated": "Updated",
    "timeRange": "Time range",
    "loadingCard": "Loading {{ title }}",
    "noDataYet": "No data yet",
    "cardError": {
      "title": "Couldn’t load this card",
      "text": "The service didn’t respond. Other cards are unaffected."
    },
    "all": "All",
    "now": "Now",
    "freshness": "fresh to 1 min",
    "errorRate": "Error rate",
    "requestsEvaluated": "requests evaluated",
    "open": "open",
    "noneOpen": "None open",
    "ended": "ended",
    "past24hFixed": "Past 24h · fixed window",
    "past3dMaximum": "Past 3 days · Log Insights maximum",
    "anomaly": "Anomaly",
    "anomalyHint": "This hour's log flow rate is {{ deviation }} from its 24-hour average. The tag shows when the gap is {{ threshold }} or more.",
    "flowAverage": "24h average",
    "thisHourIs": "this hour is",
    "linesPerSec": "lines/s",
    "aboveBaseline": "above baseline",
    "belowBaseline": "below baseline",
    "investigation": "Investigation",
    "investigating": "Investigating…",
    "step": "Step",
    "learningBaseline": "Learning baseline",
    "baselineReadyLater": "Learning baseline · counts appear once it is ready",
    "newPatterns": "new patterns",
    "changed": "changed",
    "changedHint": "Error patterns seen before this range whose volume moved sharply {{ vs }}, as flagged by Log Insights.",
    "newPatternsInBucket": "{{count}} new pattern · {{ from }}–{{ to }}",
    "newPatternsInBucket_plural": "{{count}} new patterns · {{ from }}–{{ to }}",
    "denied": "denied",
    "deniedByPolicy": "Denied by policy",
    "mostRecentFirst": "Most recent first",
    "longestRunningFirst": "Longest running first",
    "calls": "calls",
    "latencyTiers": "Tiers: under 750 ms · 750 ms–1 s · over 1 s",
    "sections": {
      "assistant": "Aura assistant",
      "needsAttention": "Needs attention",
      "healthVolume": "Health and volume",
      "whatChanged": "What changed",
      "atAGlance": "At a glance",
      "mcp": "Mezmo MCP"
    },
    "mcpBanner": {
      "title": "Get these insights and more in your AI tools",
      "text": "The Mezmo MCP server brings your logs, pipelines, traces, metrics and agent activity to any assistant that speaks the Model Context Protocol.",
      "link": "Read the MCP docs"
    },
    "products": {
      "logAnalysis": "Log Analysis",
      "logInsights": "Log Insights",
      "pipelines": "Pipelines",
      "pipelinesByBytes": "Pipelines · by bytes",
      "governance": "AI Governance",
      "metricsInsights": "Metrics Insights",
      "traceInsightsP95": "Trace Insights · p95"
    },
    "cards": {
      "logLines": "Log lines",
      "logErrors": "Log errors",
      "pipelineVolume": "Pipeline volume",
      "enforcement": "Enforcement outcomes",
      "anomalies": "Anomalies",
      "flowRate": "Flow rate",
      "latestInvestigation": "Latest investigation",
      "patterns": "New log patterns",
      "blockedTools": "Most blocked tools",
      "topDestinations": "Top destinations",
      "pipelineErrors": "Latest pipeline errors",
      "slowDependencies": "Slowest dependencies",
      "openAnomalies": "Open anomalies"
    },
    "charts": {
      "logLines": "Log lines per bucket, with warning and error share",
      "logErrors": "Errors per bucket over the range",
      "pipelineVolume": "Ingress, egress and internal bytes over time",
      "enforcement": "Allow, requires approval and deny per bucket",
      "anomalies": "Anomalies over time, open versus ended",
      "flowRate": "Hourly flow rate against its moving average",
      "patterns": "New log patterns per bucket over the range"
    },
    "legend": {
      "allLines": "All lines",
      "warning": "Warning",
      "error": "Error",
      "errors": "Errors",
      "newPatternsPer": "New patterns per {{ bucket }}",
      "hourlyRate": "Hourly rate",
      "movingAverage": "Moving average",
      "ingress": "Ingress",
      "egress": "Egress",
      "internal": "Internal",
      "allow": "Allow",
      "approval": "Approval",
      "deny": "Deny"
    },
    "links": {
      "exploreLogs": "Explore logs",
      "viewErrors": "View errors",
      "openPipelines": "Open pipelines",
      "viewPolicies": "View policies",
      "reviewPolicies": "Review policies",
      "openMetrics": "Open Metrics Insights",
      "viewAnomalies": "View anomalies",
      "viewThroughput": "View throughput",
      "openInvestigations": "Open investigations",
      "setUpLogInsights": "Set up Log Insights",
      "allPatterns": "All patterns",
      "allDestinations": "All destinations",
      "allPipelines": "All pipelines",
      "openTraces": "Open Trace Insights",
      "addOtelSource": "Add an OpenTelemetry source"
    },
    "empty": {
      "logLines": {
        "title": "No log lines in this range",
        "text": "Lines appear here once logs are ingested."
      },
      "pipelineVolume": {
        "title": "No pipeline traffic yet",
        "text": "Deploy a pipeline to see ingress and egress here."
      },
      "enforcement": {
        "title": "No decisions yet",
        "text": "Enforcement outcomes appear once agents call tools under a policy."
      },
      "anomalies": {
        "title": "No anomalies in this range",
        "text": "Metrics Insights flagged nothing in the selected range."
      },
      "flowRate": {
        "title": "No flow rate yet",
        "text": "The rate appears once lines are flowing into Log Analysis."
      },
      "investigations": {
        "title": "No investigations yet",
        "text": "Ask Aura above, or let an alert start one automatically."
      },
      "patterns": {
        "title": "No distilled data yet",
        "text": "Log Insights groups lines into patterns once logs flow through a distill step."
      },
      "blockedTools": {
        "title": "Nothing blocked",
        "text": "No tool calls were denied by policy in this range."
      },
      "destinations": {
        "title": "No egress in this range",
        "text": "Destinations appear once pipelines send data out."
      },
      "pipelineErrors": {
        "title": "No pipeline errors",
        "text": "None of the pipelines are reporting errors."
      },
      "noOtel": {
        "title": "No OpenTelemetry source yet",
        "text": "Send traces through a pipeline to see services and their dependencies."
      },
      "dependencies": {
        "title": "No dependencies yet",
        "text": "Dependencies appear once traced services call each other."
      },
      "openAnomalies": {
        "title": "No open anomalies",
        "text": "Every anomaly flagged in the past day has ended."
      }
    },
    "tiles": {
      "couldNotLoad": "Couldn’t load",
      "live": "LIVE",
      "investigations": "Investigations",
      "active": "active",
      "investigating": "investigating",
      "triggered": "triggered",
      "lastCompleted": "Last completed",
      "noInvestigations": "No investigations yet",
      "pendingApprovals": "Pending approvals",
      "oldestWaiting": "Oldest waiting",
      "noneWaiting": "None waiting",
      "metricAnomalies": "Metric anomalies",
      "open": "open",
      "flaggedToday": "flagged today",
      "dependencies": "Dependencies",
      "failing": "failing",
      "slow": "slow",
      "p95Over": "p95 over 1 s",
      "noneSlow": "None slow",
      "pipelines": "Pipelines",
      "inError": "in error",
      "openIncidents": "{{count}} open incident",
      "openIncidents_plural": "{{count}} open incidents",
      "noIncidents": "No open incidents",
      "planUsage": "Plan usage",
      "overQuota": "Over quota",
      "resets": "Resets"
    },
    "glance": {
      "agentCoverage": "Agent coverage",
      "automationRate": "Automation rate",
      "meanTimeToDecide": "Mean time to decide",
      "pipelines": "Pipelines",
      "deployed": "deployed",
      "draft": "draft",
      "paused": "paused",
      "suspended": "suspended",
      "edgeDown": "Edge instances down",
      "mutedAlerts": "Muted alerts",
      "retention": "Retention",
      "days": "days",
      "archiveActive": "Archive active",
      "restoreJobs": "Restore jobs",
      "running": "running",
      "services": "services",
      "dependencies": "dependencies",
      "view": "view",
      "board": "board",
      "screen": "screen"
    },
    "upsell": {
      "title": "See volume and errors for every log line",
      "text": "Log lines, error trends and error patterns appear here on paid plans. Your pipelines keep running either way.",
      "comparePlans": "Compare plans",
      "learnMore": "Learn more"
    },
    "ask": {
      "title": "What should we look into?",
      "placeholder": "Ask Aura to investigate your logs, pipelines, metrics…",
      "description": "Ask about your logs, pipelines, traces, metrics and agents. Aura can run an investigation and report back."
    },
    "ranges": {
      "1h": {
        "title": "Past hour",
        "vs": "vs previous hour",
        "short": "this hour"
      },
      "6h": {
        "title": "Past 6 hours",
        "vs": "vs previous 6h",
        "short": "in 6h"
      },
      "24h": {
        "title": "Past 24 hours",
        "vs": "vs previous 24h",
        "short": "in 24h"
      },
      "3d": {
        "title": "Past 3 days",
        "vs": "vs previous 3d",
        "short": "in 3d"
      }
    }
  }
}
